
import React, { useCallback, useEffect, useState } from 'react'
import { ensureHexFormat } from 'smartstream-widgets/utils/colors'
import { compose } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import {getPropertiesByDualShape, getPropertiesBySingleShape, shouldRenderSvg } from './utils'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'
import { Props } from './types'
import { CompetitorBrand } from 'smartstream-widgets/types/competitor'

const styles = (theme: Theme) => createStyles({
  divFlag: {
    position: 'relative',
    height: '100%',
    width: '100%',
    '&::after': {
      content: '""',
      width: '100%',
      height: '100%',
      clipPath: 'polygon(101% -1%, 101% 101%, -1% 101%)',
      backgroundColor: theme.palette.background.default,
      position: 'absolute',
      top: 0,
      left: 0,

      '$divFlagFlipped&': {
        clipPath: 'polygon(-1% -1%, 101% 101%, -1% 101%)'
      },
    }
  },
  divFlagFlipped: {},
  flagContent: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
  },
  flagContent2: {
    display: 'block',
    width: '100%',
    height: '100%',
    clipPath: 'polygon(85% 0, 90% 0, 0 90%, 0 85%)',
    backgroundColor: theme.palette.background.default,

    '$divFlagFlipped &': {
      clipPath: 'polygon(10% 0, 15% 0, 100% 85%, 100% 90%)'
    },
  },
  svgFlag: {
    display: 'block',
    height: '100%',
    width: '100%'
  },
  logo: {
    display: 'block',
    height: '100%',
    objectFit: 'contain',
    width: '100%'
  },
  logoContainer: {
    background: theme.palette.common.white,
    borderRadius: '50%',
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.2)',
    boxSizing: 'border-box',
    position: 'absolute',
    transform: 'translate(-50%,-50%)'
  },
  root: {
    position: 'relative'
  },
  logoFlagSize: (props: Props) => {
    const { competitorsBrand, shape } = props
    const isDualCompetitor = competitorsBrand && competitorsBrand.length === 2
    if(isDualCompetitor) {
      return {
        width: shape === 'diagonal' ? 44 : 84,
        height: shape === 'diagonal' ? 33 : 41
      }
    } else {
      return {
        width: shape === 'diagonal' ? 30 : 84,
        height: shape === 'diagonal' ? 30 : 41
      }      
    }
  },
  logoSize: (props: Props) => {
    const { competitorsBrand, shape } = props
    const isDualCompetitor = competitorsBrand && competitorsBrand.length === 2
    const isDiagonal = shape === 'diagonal'
    const dimensionValue = (isDualCompetitor && isDiagonal && 20) || (isDiagonal && 25) || 36

    return {
        width: dimensionValue,
        height: dimensionValue,
        padding : Math.round(dimensionValue * 0.125)
    }
  }
})

function LogoFlag(props: Props) {
  const {
    classes,
    className = '',
    config: {
      BASE_ASSET_PATH = '',
    },
    flip,
    hasGradient = false,
    overrides = {},
    competitorsBrand = [{}] as Array<CompetitorBrand>,
    shape = 'corner',
    theme,
    svgKey
  } = props

  const showLogos = competitorsBrand.every((brand) => brand && brand.logo)
  const isDualCompetitor = competitorsBrand && competitorsBrand.length === 2
  const [gradientIds, setGradientId] = useState(
    [`gradient-${svgKey}-1`, `gradient-${svgKey}-2`]
  )

  useEffect(
    () => {
      setGradientId([`gradient-${svgKey}-1`, `gradient-${svgKey}-2`])
    },
    [svgKey]
  )

  const competitorsBrandProcessed = competitorsBrand.map(brand => {
    const { primaryColor, secondaryColor, logo } = brand || {}
    return {
      primaryColor: ensureHexFormat(primaryColor) || theme.palette.grey['300'],
      secondaryColor: ensureHexFormat(secondaryColor) || theme.palette.grey['400'],
      logo
    }
  })

  const renderSvg = shouldRenderSvg(shape)

  const flagProperties = isDualCompetitor ?
    getPropertiesByDualShape(shape, flip) :
    getPropertiesBySingleShape(shape, flip)

  const flagPropertiesOverridden = Object.assign({}, flagProperties, overrides)

  const getSvgPath = useCallback((competitorBrand: CompetitorBrand, gradientId: string, transform: string) => {
    return (
      <g transform={transform}>
        <path
          d={flagPropertiesOverridden.primaryPath}
          fill={
            hasGradient && gradientId ?
              `url(#${gradientId})` :
              competitorBrand.primaryColor
          }
        />
        <path
          d={flagPropertiesOverridden.secondaryPath}
          fill={competitorBrand.secondaryColor}
        />
      </g>
    )
  }, [flagPropertiesOverridden.primaryPath, flagPropertiesOverridden.secondaryPath, hasGradient])

  const getLogoMarkup = useCallback((competitorBrand: CompetitorBrand, left: string, top: string = '50%') => {
    const { logo } = competitorBrand
    return (
      logo &&
      <div
        className={`${classes.logoContainer} ${classes.logoSize}`}
        style={{
          left,
          top
        }}
      >
        <img
          className={classes.logo}
          src={logo.svg || logo.x1 || `${BASE_ASSET_PATH}/img/defaultLogo.svg`}
        />
      </div>
    )
  }, [classes.logoSize, classes])

  const getCssLinearGradient = useCallback((competitorBrand: CompetitorBrand, flip: boolean) => {
    const { primaryColor } = competitorBrand
    return `linear-gradient(to ${flip ? 'left': 'right' } top, ${primaryColor} 0%, ${primaryColor}cc 100%)`
  }, [])

  const getSvgLinearGradient = useCallback((competitorBrand: CompetitorBrand, gradientId: string) => {
    const { primaryColor } = competitorBrand
    return (
      <linearGradient
        x1='0'
        x2='0'
        y1='0'
        y2='1'
        id={gradientId}
      >
        <stop
          stopColor={primaryColor}
          offset='0%' stopOpacity='0.8'
        />
        <stop
          stopColor={primaryColor}
          offset='100%'
        />
      </linearGradient>
    )
  }, [])

  return (
    <div
      className={`${classes.root} ${className} ${classes.logoFlagSize}`} data-testid='logo-flag'
    >
      {
        renderSvg &&
        <svg
            className={classes.svgFlag}
            preserveAspectRatio='none'
            viewBox='0 0 100 100'>
          {
            hasGradient &&
            <defs>
              {getSvgLinearGradient(competitorsBrandProcessed[0], gradientIds[0])}
              {isDualCompetitor && getSvgLinearGradient(competitorsBrandProcessed[1], gradientIds[1])}
            </defs>
          }
          <g transform={flip ? 'scale(-1 1) translate(-100 0)' : undefined}>
            {
              getSvgPath(
                  competitorsBrandProcessed[0],
                  gradientIds[0],
                  flip && isDualCompetitor && shape === 'diagonal' ?
                      'translate(28.57 0)' :
                      undefined
              )
            }
            {
              isDualCompetitor && shape === 'diagonal' &&
              getSvgPath(
                  competitorsBrandProcessed[1],
                  gradientIds[1],
                  !flip ? 'translate(28.57 0)' : undefined
              )
            }
          </g>
        </svg>
      }
      {
        !renderSvg &&
        <div className={`${classes.divFlag} ${flip ? classes.divFlagFlipped : ''}`}>
          <div
              className={classes.flagContent}
              style={{background: hasGradient
                    ? getCssLinearGradient(competitorsBrandProcessed[0], flip)
                    : competitorsBrandProcessed[0].primaryColor
              }}
          >
          </div>
          <div
              className={classes.flagContent2}
              style={{background: competitorsBrandProcessed[0].secondaryColor}}
          >
          </div>
        </div>
      }
      {showLogos && (!isDualCompetitor || shape === 'corner') &&
        getLogoMarkup(
          competitorsBrandProcessed[0],
          flagPropertiesOverridden.logoAlignment
        )
      }

      {
        showLogos && isDualCompetitor && shape === 'diagonal' &&
        <div>
          {
            getLogoMarkup(
              competitorsBrandProcessed[0],
              flagPropertiesOverridden.leftLogo && flagPropertiesOverridden.leftLogo.left,
              flagPropertiesOverridden.leftLogo && flagPropertiesOverridden.leftLogo.top
            )
          }

          {
            getLogoMarkup(
              competitorsBrandProcessed[1],
              flagPropertiesOverridden.rightLogo && flagPropertiesOverridden.rightLogo.left,
              flagPropertiesOverridden.rightLogo && flagPropertiesOverridden.rightLogo.top
            )
          }
        </div>
      }
    </div>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'LogoFlag', withTheme: true, defaultTheme: getDefaultTheme})
)(LogoFlag)
